import apiClient from '../../shared/services/api';
import config from '../../config/environment';
import {
  ClientListItem,
  ClientDetails,
  CustomerActivity,
  CustomerReminder,
  buildCreateCustomerCommand,
  buildUpdateCustomerCommand,
} from '../models/Client';

const BASE = config.API_ENDPOINTS.CUSTOMERS;

const mapSpringPage = (data, mapper) => ({
  items: (data.content || []).map(mapper),
  pagination: {
    page: data.number ?? 0,
    size: data.size ?? 20,
    totalElements: data.totalElements ?? 0,
    totalPages: data.totalPages ?? 0,
  },
});

const buildClientsQueryParams = ({ page = 0, size = 20, search, statuses, cities, sort } = {}) => {
  const queryParams = new URLSearchParams();
  queryParams.append('page', String(page));
  queryParams.append('size', String(size));

  if (search?.trim()) {
    queryParams.append('search', search.trim());
  }

  if (Array.isArray(statuses)) {
    statuses.forEach((status) => queryParams.append('statuses', status));
  }

  if (Array.isArray(cities)) {
    cities.forEach((city) => queryParams.append('cities', city));
  }

  if (sort?.field && sort?.direction) {
    queryParams.append('sort', `${sort.field},${sort.direction}`);
  }

  return queryParams;
};

export class CrmService {
  async getClients(params = {}) {
    const response = await apiClient.get(BASE, {
      params: buildClientsQueryParams(params),
    });
    const mapped = mapSpringPage(response.data, ClientListItem.fromApi);

    return {
      clients: mapped.items,
      pagination: mapped.pagination,
    };
  }

  async getClientDetails(clientId) {
    const response = await apiClient.get(`${BASE}/${clientId}`);
    return ClientDetails.fromApi(response.data);
  }

  async getActivities(clientId, params = {}) {
    const { page = 0, size = 50 } = params;
    const response = await apiClient.get(`${BASE}/${clientId}/activities`, {
      params: {
        page,
        size,
        sort: 'createdAt,desc',
      },
    });
    const mapped = mapSpringPage(response.data, CustomerActivity.fromApi);
    return mapped.items;
  }

  async addActivity(clientId, { type, comment, remindAt }) {
    const payload = { type, comment };
    if (remindAt) {
      payload.remindAt = remindAt;
    }
    await apiClient.post(`${BASE}/${clientId}/activities`, payload);
  }

  async createCustomer(customerData) {
    const payload = buildCreateCustomerCommand(customerData);
    const response = await apiClient.post(BASE, payload);
    return response.data;
  }

  async updateCustomer(clientId, customerData) {
    const payload = buildUpdateCustomerCommand(customerData);
    await apiClient.patch(`${BASE}/${clientId}`, payload);
  }

  async getActiveReminder(customerId) {
    try {
      const response = await apiClient.get(`${BASE}/${customerId}/reminder`);
      return CustomerReminder.fromApi(response.data);
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async markReminderAsSeen(reminderId) {
    await apiClient.patch(`${BASE}/reminders/${reminderId}/seen`);
  }

  async getGusCompanyByNip(nip) {
    const normalizedNip = String(nip || '').replace(/\D/g, '');
    const response = await apiClient.get(`${BASE}/gus/${normalizedNip}`);
    return response.data;
  }

  async getCustomerCities() {
    const response = await apiClient.get(`${BASE}/cities`);
    return (response.data || []).map((item) => ({
      city: item.city || '',
      count: item.count ?? 0,
    }));
  }
}

export const crmService = new CrmService();
